import {
  Component,
  EventEmitter,
  forwardRef,
  Input,
  OnDestroy,
  WritableSignal,
  signal,
  Output,
  Signal,
  computed,
} from '@angular/core';
import { CommonModule } from '@angular/common';
import { GalaxyAbstractFilterInput, GalaxyFilterValueInterface } from '../interface';
import { GalaxyDateDefault } from '../enums';
import { GalaxyFilterOperator } from '../enums';
import { GalaxyFormFieldModule } from '@vendasta/galaxy/form-field';
import { combineLatest, map, Observable, startWith, Subscription, tap } from 'rxjs';
import { dateDefaultToLabel } from './date-default-labels';
import { FormControl, ReactiveFormsModule } from '@angular/forms';
import { TranslateModule } from '@ngx-translate/core';
import { MatSelectModule } from '@angular/material/select';
import { MatButtonModule } from '@angular/material/button';
import { MatInputModule } from '@angular/material/input';
import { DatePickerSelection, GalaxyDatepickerModule } from '@vendasta/galaxy/datepicker';
import { MatDatepickerModule } from '@angular/material/datepicker';

export interface DateOption {
  dateOption: GalaxyDateDefault;
  label: string;
}

interface DateFilterInputState {
  operator: GalaxyFilterOperator;
}

@Component({
  selector: 'glxy-date-filter-input',
  templateUrl: './date-filter-input.html',
  styleUrls: ['./date-filter-input.scss'],
  providers: [{ provide: GalaxyAbstractFilterInput, useExisting: forwardRef(() => GalaxyDateFilterInputComponent) }],
  imports: [
    CommonModule,
    TranslateModule,
    MatSelectModule,
    MatButtonModule,
    MatInputModule,
    GalaxyFormFieldModule,
    ReactiveFormsModule,
    GalaxyDatepickerModule,
    MatDatepickerModule,
  ],
  standalone: true,
})
export class GalaxyDateFilterInputComponent implements GalaxyAbstractFilterInput, OnDestroy {
  dateSelectionType: DatePickerSelection = DatePickerSelection.single;

  state: WritableSignal<DateFilterInputState> = signal({
    operator: GalaxyFilterOperator.FILTER_OPERATOR_IS,
  });

  @Input() set operator(value: GalaxyFilterOperator) {
    this.state.update((state) => ({ ...state, operator: value }));
    this.dateSelectionType =
      value === GalaxyFilterOperator.FILTER_OPERATOR_IS_BETWEEN ||
      value === GalaxyFilterOperator.FILTER_OPERATOR_IS_NOT_BETWEEN
        ? DatePickerSelection.range
        : DatePickerSelection.single;
  }

  @Input() set initialValue(value: GalaxyFilterValueInterface[]) {
    if (value?.length > 0 && value[0].date) {
      this.dateDefaultControl.setValue(GalaxyDateDefault.DATE_DEFAULT_INVALID);
      window.setTimeout(() => {
        if (value?.length > 0 && value[0].date) {
          if (value.length > 1 && value[1].date) {
            this.startDateControl.setValue(value[0].date);
            this.endDateControl.setValue(value[1].date);
          } else {
            this.dateControl.setValue(value[0].date);
          }
        }
      }, 0);
      this.valueChanges.emit(value);
    } else if (value?.length > 0 && value[0].dateDefault) {
      window.setTimeout(() => {
        if (value?.length > 0 && value[0].dateDefault) {
          this.dateDefaultControl.setValue(value[0].dateDefault, { emitEvent: true });
        }
      }, 0);
      this.valueChanges.emit(value);
    }
  }

  @Input() set disabled(value: boolean) {
    if (value) {
      this.dateControl.disable();
      this.startDateControl.disable();
      this.endDateControl.disable();
      this.dateDefaultControl.disable();
    }
  }

  @Output() valueChanges = new EventEmitter<GalaxyFilterValueInterface[]>();

  dateDefaultOptions: Signal<DateOption[]> = computed(() => {
    const operator = this.state().operator;
    if (
      operator === GalaxyFilterOperator.FILTER_OPERATOR_IS_BETWEEN ||
      operator === GalaxyFilterOperator.FILTER_OPERATOR_IS_NOT_BETWEEN
    ) {
      this.dateDefaultControl.setValue(GalaxyDateDefault.DATE_DEFAULT_INVALID);
      return dateDefaultToLabel.filter((option) => option.dateOption === GalaxyDateDefault.DATE_DEFAULT_INVALID);
    }
    return dateDefaultToLabel;
  });

  dateDefaultControl: FormControl<GalaxyDateDefault> = new FormControl<GalaxyDateDefault>(
    GalaxyDateDefault.DATE_DEFAULT_INVALID,
    {
      nonNullable: true,
    },
  );

  dateControl: FormControl<Date | undefined> = new FormControl<Date | undefined>(undefined, { nonNullable: true });
  startDateControl: FormControl<Date | undefined> = new FormControl<Date | undefined>(undefined, { nonNullable: true });
  endDateControl: FormControl<Date | undefined> = new FormControl<Date | undefined>(undefined, { nonNullable: true });

  showDate$: Observable<boolean> = this.dateDefaultControl.valueChanges.pipe(
    startWith(GalaxyDateDefault.DATE_DEFAULT_INVALID),
    map((dateDefault) => {
      return dateDefault === GalaxyDateDefault.DATE_DEFAULT_INVALID;
    }),
  );

  showDateRangePicker: Signal<boolean> = computed(() => {
    const operator = this.state().operator;
    return (
      operator === GalaxyFilterOperator.FILTER_OPERATOR_IS_BETWEEN ||
      operator === GalaxyFilterOperator.FILTER_OPERATOR_IS_NOT_BETWEEN
    );
  });

  subscriptions: Subscription[] = [];

  constructor() {
    this.subscriptions.push(
      this.dateDefaultControl.valueChanges
        .pipe(
          tap((value) => {
            if (value !== GalaxyDateDefault.DATE_DEFAULT_INVALID) {
              this.valueChanges.emit([{ dateDefault: value }]);
            }
          }),
        )
        .subscribe(),
    );

    this.subscriptions.push(
      this.dateControl.valueChanges
        .pipe(
          tap((value) => {
            if (value) {
              this.valueChanges.emit([{ date: value }]);
            }
          }),
        )
        .subscribe(),
    );

    this.subscriptions.push(
      combineLatest([this.startDateControl.valueChanges, this.endDateControl.valueChanges])
        .pipe(
          tap(([startDate, endDate]) => {
            if (startDate && endDate) {
              this.valueChanges.emit([{ date: startDate }, { date: endDate }]);
            }
          }),
        )
        .subscribe(),
    );
  }

  ngOnDestroy(): void {
    this.subscriptions.forEach((subscription) => subscription.unsubscribe());
  }

  clearValue() {
    this.dateDefaultControl.setValue(GalaxyDateDefault.DATE_DEFAULT_INVALID);
    this.dateControl.setValue(undefined);
  }
}
